<template>
  <div v-rect-loading="loading" class="mobile-resume-layout">
    <ThirdImport
      ref="thirdImport"
      :show="showThirdImportPage"
      :type="thirdType"
      :resumeEditorSetting="resumeEditorSetting"
      :companyId="companyId"
      :jobId="jobId"
      @goBack="cancelThirdImport"
      @load="changeLoading"
      @uploadResume="handleUploadResume"
      @change="formatResumeData"
    />
    <div v-show="!showThirdImportPage && currentStep" class="resume-container">
      <van-nav-bar
        safe-area-inset-top
        fixed
        z-index="1003"
        class="nav-bar"
        :title="getNavBarTitle()"
        :left-arrow="!hiddenBackBtn"
        @click-left="onClickLeft"
        @click-right="onClickRight"
      >
        <template #right>
          <van-icon v-if="showUploadButton && currentStep !== UPLOAD_RESUME" class="nav-right-icon iconfont icona-ImportResume" />
        </template>
      </van-nav-bar>
      <div v-if="headImgStyle" class="company-head-img" :style="headImgStyle"></div>
      <div class="content">
        <Steps
          v-if="stepTypes.length > 1"
          class="step"
          ref="activeStep"
          :stepTypes="getStepMenu()"
          :active="active"
          @onClick="clickStep"
        />
        <div class="avatar" v-if="showAvatar.includes(currentStep)">
          <van-uploader
            :after-read="uploadPhoto"
          >
            <van-image
              round
              class="image"
              fit="cover"
              :src="getPhotoPath(resumeInfo.baseInfo?.photo || '/static/images_en/default.jpg')"
            />
          </van-uploader>
        </div>

        <ImportResume
          v-if="stepTypes.includes(UPLOAD_RESUME)"
          :show="currentStep === UPLOAD_RESUME"
          :resumeEditorSetting="resumeEditorSetting"
          :needUploadResume="needUploadResume"
          :personalInfo="personalInfo"
          :companyId="companyId"
          @change="formatResumeData"
          @uploadResume="handleUploadResume"
          @load="changeLoading"
          @handleThirdImport="handleThirdImport"
          @create="handleCreateNewResume"
          @skip="goSkip"
          @back="goBack"
        >
        </ImportResume>

        <BasicInformation
          v-if="stepTypes.includes(BASE_INFO)"
          :show="currentStep === BASE_INFO"
          :ref="`resumeFormEditor_${BASE_INFO}`"
          :baseInfo="resumeInfo.baseInfo"
          :educationInfo="resumeInfo.educationInfo"
          :workExperienceInfo="resumeInfo.workExperienceInfo"
          :resumeEditorSetting="resumeEditorSetting"
          :companyId="companyId"
          :jobId="jobId"
          @load="changeLoading"
        />

        <WorkExperience
          v-if="stepTypes.includes(WORK_EXPERIENCE)"
          :show="currentStep === WORK_EXPERIENCE"
          :ref="`resumeFormEditor_${WORK_EXPERIENCE}`"
          :data="resumeInfo.workExperienceInfo"
          :resumeEditorSetting="resumeEditorSetting"
          @load="changeLoading"
        />

        <EducationLevel
          v-if="stepTypes.includes(EDUCATION)"
          :show="currentStep === EDUCATION"
          :ref="`resumeFormEditor_${EDUCATION}`"
          :data="educationLevel"
          :resumeEditorSetting="resumeEditorSetting"
          @load="changeLoading"
        />

        <Additional
          v-if="stepTypes.includes(ADDITIONAL_INFO)"
          :show="currentStep === ADDITIONAL_INFO"
          :ref="`resumeFormEditor_${ADDITIONAL_INFO}`"
          :data="resumeInfo.additionalInfo"
          :resumeEditorSetting="resumeEditorSetting"
        />

        <ResumeInfo
          v-if="currentStep === RESUME_INFO"
          ref="resumeInfo"
          :photo="resumeInfo.baseInfo?.photo"
          :resumeEditorSetting="resumeEditorSetting"
          @load="changeLoading"
        />

        <JobQuestion
          v-if="stepTypes.includes(JOB_QUESTIONNAIRE)"
          :show="currentStep === JOB_QUESTIONNAIRE"
          :ref="`jobQuestionFormEditor_${JOB_QUESTIONNAIRE}`"
          class="question-form"
          :class="stepTypes.length <= 1 && 'no-step'"
          :resumeEditorSetting="resumeEditorSetting"
          :data="resumeInfo.openQuestion.standard"
          @load="changeLoading"
        />

        <JobQuestion
          v-if="stepTypes.includes(INVITE_QUESTIONNAIRE)"
          :show="currentStep === INVITE_QUESTIONNAIRE"
          :ref="`jobQuestionFormEditor_${INVITE_QUESTIONNAIRE}`"
          class="question-form"
          :class="stepTypes.length <= 1 && 'no-step'"
          :resumeEditorSetting="resumeEditorSetting"
          :data="resumeInfo.openQuestion.invite"
          @load="changeLoading"
        />

        <ReferralResume
          v-if="currentStep === SUBMIT_REFERRAL"
          ref="referralResume"
          :resumeEditorSetting="resumeEditorSetting"
          @load="changeLoading"
        />

        <div class="btn-group" v-if="currentStep !== UPLOAD_RESUME">
          <van-button
            v-if="active > 0"
            class="common-btn prev-btn"
            @click="onClickPrevBtn"
          >
          {{ $t(`common.btn.prev`) }}
          </van-button>
          <van-button
            v-if="currentStep !== UPLOAD_RESUME"
            class="common-btn"
            @click="submit(true)"
          >
          {{ $t(`common.btn.${submitBtnLabel}`) }}
          </van-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import Steps from '@/components/mobile/customSteps.vue'
import ResumeInfo from './resumeInfo.vue'
import BasicInformation from './basicInformation.vue'
import WorkExperience from './workExperienceInfo.vue'
import EducationLevel from './educationInfo.vue'
import Additional from './additional.vue'
import { mapActions } from 'vuex'
import JobQuestion from './jobQuestion.vue'
import { STEP_TYPE } from '@/constant/candidate'
import ImportResume from './importResume.vue'
import ThirdImport from './thirdImport.vue'
import ReferralResume from './referralResume.vue'
import { getFilePath } from '@/utils/qiniu'

export default {
  components: {
    Steps,
    ResumeInfo,
    BasicInformation,
    WorkExperience,
    EducationLevel,
    Additional,
    JobQuestion,
    ImportResume,
    ThirdImport,
    ReferralResume
  },
  props: {
    resumeInfo: {
      type: Object,
      default: null
    },
    resumeEditorSetting: {
      type: Object,
      required: true
    },
    hiddenSteps: {
      type: Array,
      default: () => ([])
    },
    needUploadResume: {
      default: false
    },
    showUploadButton: {
      default: true
    },
    hasOpenQuestion: {
      default: false
    },
    isEditMode: {
      default: false
    },
    isAddMode: {
      default: false
    },
    isMiniResume: {
      default: false
    },
    isReferral: {
      default: false
    },
    personalInfo: {
      type: Object
    },
    customPageInfo: {
      default: {}
    },
    handlerSaveOpenQuestion: {
      type: Function
    },
    handlerSaveInviteQuestion: {
      type: Function
    },
    hiddenBackBtn: {
      type: Boolean,
      default: false
    },
    isFromPreference: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      ...STEP_TYPE,
      showAvatar: [
        STEP_TYPE.BASE_INFO,
        STEP_TYPE.EDUCATION,
        STEP_TYPE.WORK_EXPERIENCE
        // STEP_TYPE.ADDITIONAL_INFO
      ],
      stepList: [
        STEP_TYPE.UPLOAD_RESUME,
        STEP_TYPE.BASE_INFO,
        STEP_TYPE.WORK_EXPERIENCE,
        STEP_TYPE.EDUCATION,
        // STEP_TYPE.ADDITIONAL_INFO,
        STEP_TYPE.SUBMIT_REFERRAL,
        STEP_TYPE.JOB_QUESTIONNAIRE,
        STEP_TYPE.INVITE_QUESTIONNAIRE,
        STEP_TYPE.RESUME_INFO
      ],
      miniResumeHiddenStep: [STEP_TYPE.WORK_EXPERIENCE, STEP_TYPE.EDUCATION, STEP_TYPE.LANGUAGE, STEP_TYPE.ADDITIONAL_INFO],
      submittedStep: [],
      loading: false,
      skipParseResume: false,
      finishResume: null,
      currentStep: null,
      showThirdImportPage: false,
      useThirdPartyUpload: false,
      thirdType: null,
      avatarPhoto: null,
      updateResumePromise: null,
      updateResumePromiseResolve: null
    }
  },
  computed: {
    educationLevel () {
      return {
        educationInfo: this.resumeInfo?.educationInfo,
        languageInfo: this.resumeInfo?.languageInfo
      }
    },
    saveBtnTextKey () {
      return this.applyUrl ? 'common.btn.apply' : null
    },
    onlyUploadResume () {
      return this.$route.query.only_upload_resume === '1'
    },
    isBasicResume () {
      return this.$route.meta.isBasicResume
    },
    applyUrl () {
      return this.$route.query.apply_url
    },
    jobId () {
      // TODO not use,  jobId in the path query is a bad param
      return this.$route.query.job_id || this.$route.query.jobId
    },
    companyId () {
      return this.$route.query.company_id
    },
    back () {
      return this.$route.query.back
    },
    inviteUploadResumeHistoryId () {
      return this.$route.query.history_id
    },
    stepTypes () {
      const stepTypes = []
      _.forEach(this.stepList, (value) => {
        if (_.keys(STEP_TYPE).includes(value) && !this.hiddenSteps.includes(value)) {
          stepTypes.push(value)
        }
      })
      return stepTypes
    },
    active () {
      return _.indexOf(this.stepTypes, this.currentStep)
    },
    isLastStep () {
      return this.stepTypes.indexOf(this.currentStep) === this.stepTypes.length - 1
    },
    submitBtnLabel () {
      if (this.isReferral) {
        return 'submit'
      }
      return this.isLastStep ? (this.jobId ? 'apply' : 'save') : 'next'
    },
    headImgStyle () {
      const headerImgSrc = this.customPageInfo?.has_custom_mobile_landing_page ? getFilePath(this.customPageInfo?.header_image) : this.customPageInfo?.default_header_image
      if (headerImgSrc) {
        return {
          backgroundImage: `url(${headerImgSrc})`
        }
      }
      return null
    }
  },
  created () {
    const baseInfo = this.resumeInfo?.baseInfo
    if (baseInfo && !baseInfo.emailAddress) {
      this.resumeEditorSetting[STEP_TYPE.BASE_INFO].canEditEmail = true
    }
  },
  mounted () {
  },
  methods: {
    ...mapActions('personal', [
      'getResumeData',
      'createOrUpdateResumeData',
      'uploadCandidatePhoto',
      'getMiniResumeData',
      'createOrUpdateMiniResumeData',
      'getBasicResumeData',
      'createOrUpdateBasicResumeData',
      'personalUploadResume'
    ]),
    ...mapActions('application', ['getJobApplyRequiredInfo']),
    ...mapActions('candidate', ['getFullResumeInfo']),
    onClickLeft () {
      this.changeRoute(true)
    },
    onClickRight () {
      if (this.hiddenSteps.includes(STEP_TYPE.UPLOAD_RESUME)) {
        this.hiddenSteps.splice(this.hiddenSteps.indexOf(STEP_TYPE.UPLOAD_RESUME), 1)
      }
      this.$nextTick(() => {
        this.$router.push({ query: { ...this.$route.query, step: STEP_TYPE.UPLOAD_RESUME } })
      })
    },
    changeRoute (back) {
      if (!back && this.isLastStep) {
        return
      }
      if (back) {
        this.$router.go(-1)
      } else {
        this.$router.push({ query: { ...this.$route.query, step: this.stepTypes[this.active + 1] } })
      }
    },
    async onClickPrevBtn () {
      if (this.active > 0) {
        this.$router.push({ query: { ...this.$route.query, step: this.stepTypes[this.active - 1] } })
      }
    },
    async clickStep (target) {
      if ((this.stepTypes[this.active] === this.UPLOAD_RESUME && this.needUploadResume) || this.active === target) {
        return
      }
      if (target < this.active) {
        await this.submitCurrentStep(true)
        this.$router.push({ query: { ...this.$route.query, step: this.stepTypes[target] } })
      } else {
        const valid = await this.submitCurrentStep()
        if (!valid) {
          return
        }
        this.changeLoading(true)
        let targetStep = this.active
        const pastIndexList = _.range(this.active + 1, target + 1)
        _.forEach(pastIndexList, index => {
          const step = this.stepTypes[index]
          targetStep = step
          if (!this.submittedStep.includes(step)) {
            return false
          }
        })
        await this.submit()
        this.$router.push({ query: { ...this.$route.query, step: targetStep } })
        this.changeLoading(false)
      }
    },
    async submitCurrentStep (isPrevious, isSaveQuestion) {
      const ref = this.$refs[`resumeFormEditor_${this.currentStep}`] || this.$refs[`jobQuestionFormEditor_${this.currentStep}`]
      const valid = ref ? await ref.submit(isPrevious) : true
      if (valid && !isPrevious && !this.submittedStep.includes(this.currentStep)) {
        this.submittedStep.push(this.currentStep)
      } else if (!valid) {
        _.remove(this.submittedStep, (value) => value === this.currentStep)
      }
      if (isSaveQuestion && valid && ref) {
        // 保存question
        const answer = ref.getAnswer()
        if (this.currentStep === STEP_TYPE.JOB_QUESTIONNAIRE) {
          await this.handlerSaveOpenQuestion(answer, this.isLastStep)
        } else if (this.currentStep === STEP_TYPE.INVITE_QUESTIONNAIRE) {
          await this.handlerSaveInviteQuestion(answer, this.isLastStep)
        }
      }
      return valid
    },
    async handleUploadResume (data, skipParseResume) {
      if (skipParseResume) {
        this.resumeInfo.resumePath = data
        this.submit(true)
      } else {
        this.$emit('formatResumeData', data, () => {
          this.$nextTick(() => {
            this.submit(true)
          })
        })
      }
    },
    async submit (isChangeRoute) {
      const jobQuestionFormEditor = this.$refs[`jobQuestionFormEditor_${this.currentStep}`]
      const resumeFormEditor = this.$refs[`resumeFormEditor_${this.currentStep}`]
      const resumeInfo = this.$refs.resumeInfo
      const referralResume = this.$refs.referralResume
      let resumeParams = this.resumeInfo
      if (this.onlyUploadResume) {
        this.resumeInfo.isUpload = true
        resumeParams = this.resumeInfo
        resumeParams.only_upload_resume = true
        resumeParams.history_id = this.inviteUploadResumeHistoryId
      }
      if (resumeFormEditor || this.onlyUploadResume) {
        const res = resumeFormEditor ? await this.submitCurrentStep() : true
        if (res) {
          // 下一步为简历预览页、回open question时，即当前为步骤为编辑简历最后一步
          const isLastEditResume = [this.RESUME_INFO, this.JOB_QUESTIONNAIRE, this.INVITE_QUESTIONNAIRE].includes(this.stepTypes[this.active + 1])
          // 更新简历类型
          resumeParams.update_resume_step = isLastEditResume
          this.updateResumePromise = new Promise((resolve) => {
            this.updateResumePromiseResolve = resolve
          })
          this.$emit('saveResume', resumeParams, isLastEditResume, (photoPath) => {
            this.resumeInfo.baseInfo.photoFile = null
            if (photoPath) {
              this.resumeInfo.baseInfo.photo = photoPath
            }
            if (this.isBasicResume && this.isLastStep) {
              this.$emit('finishResumeOrApplyJob')
            }
            isChangeRoute && this.changeRoute()
            this.updateResumePromiseResolve()
          })
        }
        await this.updateResumePromise
        return
      }
      if (jobQuestionFormEditor) {
        const result = await this.submitCurrentStep(false, true)
        if (!result) {
          return
        }
      }
      if (referralResume) {
        await referralResume.submit()
      }
      if (resumeInfo || this.isLastStep) {
        // 简历预览页为编辑简历最后一步，若在申请流程中，则进行职位申请。若为编辑简历，则完成简历编辑
        this.$emit('finishResumeOrApplyJob')
        return
      }
      isChangeRoute && this.changeRoute()
    },
    getPhotoPath (photo) {
      if (!this.avatarPhoto && this.avatarPhoto === photo) {
        return
      }
      this.avatarPhoto = photo
      if (!process.env.production) {
        if (photo && !photo.startsWith('blob')) {
          return this.$DjangoBaseUrl(photo)
        } else {
          return photo
        }
      }
      return photo
    },
    async uploadPhoto (fileInfo) {
      const file = fileInfo.file
      const fileName = file.name
      const ext = fileName.slice(fileName.indexOf('.'))
      if (['.jpeg', '.png', '.jpg'].includes(ext.toLowerCase())) {
        this.resumeInfo.baseInfo.photoFile = file
        this.resumeInfo.baseInfo.photo = window.URL.createObjectURL(file)
      } else {
        this.$toast({ message: this.$t(`${this.i18nPrefix}.message.wrongPhotoFileType`) })
      }
    },
    handleThirdImport (type) {
      this.showThirdImportPage = true
      this.thirdType = type
    },
    cancelThirdImport (type) {
      this.showThirdImportPage = false
    },
    formatResumeData (resumeData, callback) {
      this.$emit('formatResumeData', resumeData, callback)
    },
    changeLoading (value) {
      if (value) {
        this.$eventBus.$emit('showLoading', true)
        document.body.style.overflow = 'hidden'
      } else {
        this.$eventBus.$emit('showLoading', false)
        document.body.style.overflow = ''
      }
    },
    getStepMenu () {
      return _.map(this.stepTypes, step => {
        const stepMenu = { stepType: step }
        if (this.isMiniResume && step === this.BASE_INFO) {
          stepMenu.title = this.$t(`${this.i18nPrefix}.label.stepName.MINI_BASE_INFO`)
        }
        return stepMenu
      })
    },
    getNavBarTitle () {
      if (this.isMiniResume && this.currentStep === this.BASE_INFO) {
        return this.$t(`${this.i18nPrefix}.label.stepName.MINI_BASE_INFO`)
      } else {
        return this.$t(`${this.i18nPrefix}.label.stepName.${this.currentStep}`)
      }
    },
    handleCreateNewResume () {
      if (this.isFromPreference) {
        this.$emit('changeResumeSetting')
      } else {
        this.$router.push({
          query: {
            ...this.$route.query,
            step: STEP_TYPE.BASE_INFO
          }
        })
      }
    },
    goSkip () {
      this.$emit('skip')
    },
    goBack () {
      this.$emit('back')
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler () {
        const step = this.$route.query.step
        this.currentStep = step
        if (!this.currentStep || !this.stepTypes.includes(this.currentStep)) {
          const profileStep = this.resumeEditorSetting.profileStep
          let skipStepType = this.stepTypes[0]
          if (profileStep === 2 && !this.needUploadResume) {
            skipStepType = this.stepTypes[1]
          }
          if (!this.stepTypes.includes(this.UPLOAD_RESUME)) {
            skipStepType = this.stepTypes[0]
          }
          if (!skipStepType || !_.includes(this.stepTypes, skipStepType)) {
            skipStepType = this.stepTypes[0]
          }
          this.$router.replace({ query: { ...this.$route.query, step: skipStepType } })
        }
        if (this.isEditMode) {
          this.submittedStep = _.map(this.stepTypes, step => step)
        }
        this.$nextTick(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.mobile-resume-layout {
  .resume-container {
    padding-top: 50px;
  }

  .company-head-img {
    height: 164px;
    overflow: hidden;
    background-size: cover;
    background-position: center;
    background-color: #f7f7f7;
  }

  .nav-bar {
    ::v-deep {
      .van-nav-bar__content {
        height: 50px;
        border-bottom: 1px solid #DCDFE6;

        .van-nav-bar__title {
          font-weight: 400;
          font-size: 18px;
          line-height: 22px;
          color: #303133;
        }

        .van-nav-bar__left {
          .van-icon {
            font-size: 18px;
            color: #000;
          }
        }
      }
    }

    .nav-right-icon {
      color: #000;
      font-size: 18px;
    }
  }

  .content {
    .step {
      margin: 26px 0 20px;
    }

    .avatar {
      margin-top: 4px;
      height: 58px;
      text-align: center;

      .image {
        width: 58px;
        height: 58px;
      }
    }

    .btn-group {
      padding: 0 18px;
      margin-bottom: 24px;
      padding-bottom: constant(safe-area-inset-bottom);
      padding-bottom: env(safe-area-inset-bottom);

      .common-btn {
        display: block;
        height: 36px;
        width: 100%;
        background: var(--custom-color, #0096cc);
        color: #fff;
        border: none;

        ::v-deep {
          .van-button__text {
            font-size: 14px;
          }
        }
      }

      .prev-btn {
        margin-bottom: 16px;
        color: var(--custom-color, #0096cc);
        background-color: #fff;
        border: 1px solid var(--custom-color, #0096cc);
      }
    }

    .question-form {
      padding: 8px 18px 24px;
    }

    .no-step {
      padding-top: 20px;
    }
  }
}
</style>
